import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PrimaryButton } from '@/components/PrimaryButton';
import { fetchDevicePayments, registerPaymentRefund } from '@/features/operations/api';
import {
  formatHistoryTimestamp,
  paymentStatusDetails,
  providerStatusAlerts,
} from '@/features/operations/labels';
import { formatAmount, formatPrice } from '@/features/operations/money';
import type { DevicePayment } from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

/**
 * The online charges of a device's checkouts (VEN-6). Shown only when there are any: most
 * devices are sold at the counter and have none.
 *
 * An approved payment that did not become a sale is the one thing here somebody has to act on:
 * the money has to go back to the buyer by hand, outside the system, and the refund is recorded
 * here with its reason (P-A3).
 */
/// What Mercado Pago actually charged, when it was not the USD price itself.
function chargedLabel(payment: DevicePayment) {
  return payment.chargedCurrency &&
    payment.chargedAmountMinor !== null &&
    payment.chargedCurrency !== 'USD'
    ? formatAmount(payment.chargedAmountMinor, payment.chargedCurrency)
    : null;
}

/// The line under the amount: what the state meant. Approved without a sale and refunded say it
/// in their own alert and caption, so they get none here.
function statusDetail(payment: DevicePayment) {
  if (payment.status === 'REFUNDED' || payment.refundRequirement) {
    return null;
  }
  return paymentStatusDetails[payment.status] ?? null;
}

/// Mercado Pago moved an approved payment afterwards (a refund, a chargeback, a dispute): the
/// sale stands here, so it has to be looked at there.
function providerAlert(payment: DevicePayment) {
  if (payment.status !== 'APPROVED' && payment.status !== 'REFUNDED') return null;
  return payment.providerStatus ? (providerStatusAlerts[payment.providerStatus] ?? null) : null;
}

export function DevicePayments({ token, deviceId }: { token: string; deviceId: string }) {
  const [payments, setPayments] = useState<DevicePayment[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [refunding, setRefunding] = useState<DevicePayment | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setPayments(await fetchDevicePayments(token, deviceId));
    } catch {
      setLoadError(true);
    }
  }, [token, deviceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openRefund = (payment: DevicePayment) => {
    setRefunding(payment);
    setReason('');
    setReasonError(null);
    setSaveError(null);
  };

  const handleRefund = async () => {
    if (!refunding) return;
    if (!reason.trim()) {
      setReasonError('Contá por qué se devolvió: queda registrado con tu usuario y la fecha.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await registerPaymentRefund(token, refunding.id, reason.trim());
      setPayments((current) =>
        current
          ? current.map((payment) => (payment.id === updated.id ? updated : payment))
          : current,
      );
      setRefunding(null);
    } catch {
      setSaveError('No pudimos registrar la devolución. Recargá la página y probá de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void load()}>
            Reintentar
          </Button>
        }
      >
        No pudimos cargar los pagos online de este equipo.
      </Alert>
    );
  }

  if (payments === null) {
    return <CircularProgress color="secondary" size={20} aria-label="Cargando pagos online" />;
  }

  if (!payments.length) {
    return null;
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        PAGOS ONLINE
      </Typography>
      <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }}>
        {payments.map((payment) => (
          <Stack component="li" key={payment.id} spacing={0.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
            >
              <StatusChip
                domain="payment"
                status={payment.status}
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography variant="body2">
                {formatPrice(payment.amountMinor)}
                {chargedLabel(payment) ? ` · cobrado ${chargedLabel(payment)}` : ''}
                {payment.exchangeRate
                  ? ` (cotización ${payment.exchangeRate.replace('.', ',')})`
                  : ''}
              </Typography>
            </Stack>
            {statusDetail(payment) ? (
              <Typography variant="body2">{statusDetail(payment)}</Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {payment.checkout.buyerName} · {payment.checkout.buyerEmail} ·{' '}
              {payment.approvedAt
                ? `aprobado el ${formatHistoryTimestamp(payment.approvedAt)}`
                : `iniciado el ${formatHistoryTimestamp(payment.createdAt)}`}
              {payment.providerPaymentId
                ? ` · Operación N.º ${payment.providerPaymentId} en Mercado Pago`
                : ''}
            </Typography>

            {providerAlert(payment) ? (
              <Alert severity="warning">{providerAlert(payment)}</Alert>
            ) : null}

            {payment.refundRequirement ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={() => openRefund(payment)}>
                    Registrar devolución
                  </Button>
                }
              >
                {payment.refundRequirement === 'REVERSED_SALE'
                  ? 'La venta asociada fue revertida. Devolvé el pago completo desde Mercado Pago y registralo acá.'
                  : 'Mercado Pago aprobó este pago cuando la compra ya no estaba pendiente, así que el equipo no se vendió. Devolvé el dinero desde Mercado Pago y registralo acá.'}
              </Alert>
            ) : null}

            {payment.status === 'REFUNDED' && payment.refundedAt ? (
              <Typography variant="caption" color="text.secondary">
                Devuelto el {formatHistoryTimestamp(payment.refundedAt)} por{' '}
                {payment.refundedBy?.fullName ?? 'Usuario eliminado'}: {payment.refundReason}
              </Typography>
            ) : null}
          </Stack>
        ))}
      </Stack>

      <Dialog open={refunding !== null} onClose={() => !isSaving && setRefunding(null)}>
        <DialogTitle>Registrar la devolución del pago</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Esto no devuelve el dinero: solo registra que ya lo devolviste desde Mercado Pago.
          </Typography>
          {refunding ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {refunding.checkout.buyerName} · {formatPrice(refunding.amountMinor)}
              {chargedLabel(refunding) ? ` (cobrado ${chargedLabel(refunding)})` : ''}
              {refunding.providerPaymentId ? ` · Operación N.º ${refunding.providerPaymentId}` : ''}
            </Typography>
          ) : null}
          {saveError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Motivo"
            value={reason}
            error={Boolean(reasonError)}
            helperText={reasonError ?? 'Queda registrado con tu usuario y la fecha.'}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(null);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={isSaving} onClick={() => setRefunding(null)}>
            Cancelar
          </Button>
          <PrimaryButton disabled={isSaving} onClick={() => void handleRefund()}>
            {isSaving ? 'Registrando...' : 'Registrar devolución'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
